import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api, { tapsAPI, employeesAPI, locationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { format, subDays, addDays, addHours, startOfDay, endOfDay, startOfHour } from 'date-fns';
import { fr as frLocale, es as esLocale, enUS } from 'date-fns/locale';
import { TimeRangePicker, buildPreset } from '../components/analytics/TimeRangePicker';
import { CompareMode } from '../components/analytics/CompareMode';
import { GitCompare } from 'lucide-react';
import {
    BarChart3,
    Calendar,
    Download,
    Filter,
    Wifi,
    User,
    MapPin,
    Activity,
    AlertTriangle,
    CheckCircle
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { parseUtc } from '../lib/datetime';

const Analytics = () => {
    const { t, i18n } = useTranslation();
    const [taps, setTaps] = useState([]);
    const [blocked, setBlocked] = useState({ blocked_total: 0, test_total: 0, opened_total: 0, cards: [] });
    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tapHealth, setTapHealth] = useState(null);
    const [compareOpen, setCompareOpen] = useState(false);
    const navigate = useNavigate();
    
    // Filters
    const [timeRange, setTimeRange] = useState(() => ({ ...buildPreset('last7d'), labelKey: 'last7d' }));
    const [filterEmployee, setFilterEmployee] = useState('all');
    const [filterLocation, setFilterLocation] = useState('all');
    
    const rangeMs = timeRange.end - timeRange.start;
    const rangeDays = Math.max(1, Math.round(rangeMs / 86400000));
    const isHourly = rangeMs <= 48 * 3600000;
    
    const { user, isSuperAdmin, isClientAdmin, canExportFull } = useAuth();

    useEffect(() => {
        loadData();
    }, [timeRange, filterEmployee, filterLocation]);

    useEffect(() => {
        if (isSuperAdmin()) {
            api.get('/admin/tap-health/silent-locations')
                .then(res => setTapHealth(res.data))
                .catch(() => {});
        }
    }, []);

    const loadData = async () => {
        try {
            const params = {
                limit: 500,
                start_date: timeRange.start.toISOString().slice(0, 19),
                end_date: timeRange.end.toISOString().slice(0, 19)
            };
            
            if (filterEmployee !== 'all') {
                params.employee_id = filterEmployee;
            }
            if (filterLocation !== 'all') {
                params.location_id = filterLocation;
            }

            const [tapsRes, employeesRes, locationsRes, blockedRes] = await Promise.all([
                tapsAPI.list(params),
                employeesAPI.list(),
                locationsAPI.list(),
                // Suppressed taps are NOT part of the tap total — they get their
                // own counter so a deduped repeat tap never looks like silence.
                api.get('/analytics/blocked-taps', { params: { days: 14 } }).catch(() => ({ data: null })),
            ]);
            
            setTaps(tapsRes.data);
            if (blockedRes?.data) setBlocked(blockedRes.data);
            setEmployees(sortByName(employeesRes.data));
            setLocations(sortByName(locationsRes.data));
        } catch (error) {
            toast.error(t('analytics.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Process data for charts
    const getTapsByDay = () => {
        const dateLocale = { fr: frLocale, es: esLocale }[(i18n.language || 'en').slice(0, 2)] || enUS;
        const dayMap = {};
        const fmt = isHourly ? 'MMM dd HH:00' : 'MMM dd';
        let cursor = isHourly ? startOfHour(timeRange.start) : startOfDay(timeRange.start);
        let guard = 0;
        while (cursor <= timeRange.end && guard < 750) {
            dayMap[format(cursor, fmt, { locale: dateLocale })] = 0;
            cursor = isHourly ? addHours(cursor, 1) : addDays(cursor, 1);
            guard++;
        }
        
        taps.forEach(tap => {
            const date = format(parseUtc(tap.timestamp), fmt, { locale: dateLocale });
            if (dayMap[date] !== undefined) {
                dayMap[date]++;
            }
        });
        
        return Object.entries(dayMap).map(([date, count]) => ({ date, taps: count }));
    };

    const getTapsByEmployee = () => {
        const empMap = {};
        taps.forEach(tap => {
            const name = tap.employee_name || t('analytics.unassigned');
            empMap[name] = (empMap[name] || 0) + 1;
        });
        
        return Object.entries(empMap)
            .map(([name, taps]) => ({ name, taps }))
            .sort((a, b) => b.taps - a.taps)
            .slice(0, 10);
    };

    const getTapsByPlatform = () => {
        const platMap = {};
        taps.forEach(tap => {
            const platform = tap.platform?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || t('analytics.unknown');
            platMap[platform] = (platMap[platform] || 0) + 1;
        });
        
        return Object.entries(platMap).map(([platform, taps]) => ({ platform, taps }));
    };

    const getTapsByHour = () => {
        const hourMap = {};
        for (let i = 0; i < 24; i++) {
            hourMap[i] = 0;
        }
        
        taps.forEach(tap => {
            const hour = parseUtc(tap.timestamp).getHours();
            hourMap[hour]++;
        });
        
        return Object.entries(hourMap).map(([hour, taps]) => ({
            hour: `${hour.toString().padStart(2, '0')}:00`,
            taps
        }));
    };

    const getLocationName = (locId) => {
        return locations.find(l => l.id === locId)?.name || t('analytics.unknown');
    };

    const getPlatformColor = (platform) => {
        const colors = {
            'Google Reviews': 'bg-fuchsia-100 text-fuchsia-700',
            'Facebook': 'bg-indigo-100 text-indigo-700',
            'Instagram': 'bg-pink-100 text-pink-700',
            'Trustpilot': 'bg-green-100 text-green-700'
        };
        return colors[platform] || 'bg-slate-100 dark:bg-slate-700 text-muted-foreground';
    };

    const exportData = () => {
        const csv = [
            ['Timestamp', 'Card ID', 'Employee', 'Platform', 'Location'].join(','),
            ...taps.map(tap => [
                parseUtc(tap.timestamp).toISOString(),
                tap.card_id,
                tap.employee_name || 'Unassigned',
                tap.platform,
                getLocationName(tap.location_id)
            ].join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `yulontap-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-20 rounded-xl" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="skeleton h-80 rounded-xl" />
                    <div className="skeleton h-80 rounded-xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="analytics-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('analytics.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('analytics.subtitle', { defaultValue: 'Detailed insights into your Tap Engine Card performance' })}</p>
                </div>
                {canExportFull() && (
                    <Button 
                        variant="outline" 
                        onClick={exportData}
                        data-testid="export-btn"
                        data-insight-id="analytics.export"
                        data-insight-label="Export CSV"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {t('analytics.exportCsv')}
                    </Button>
                )}
            </div>

            {/* Filters */}
            <Card data-insight-id="analytics.filters" data-insight-label="Date and filters">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <TimeRangePicker value={timeRange} onChange={setTimeRange} />

                        {locations.length > 1 && (
                            <Select value={filterLocation} onValueChange={setFilterLocation}>
                                <SelectTrigger className="w-full sm:w-48" data-testid="filter-location">
                                    <MapPin className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder={t('analytics.allLocations')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('analytics.allLocations')}</SelectItem>
                                    {locations.map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}

                        <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="filter-employee">
                                <User className="w-4 h-4 mr-2" />
                                <SelectValue placeholder={t('analytics.allEmployees')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('analytics.allEmployees')}</SelectItem>
                                {employees.map(emp => (
                                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Button
                            variant={compareOpen ? 'default' : 'outline'}
                            className="w-full sm:w-auto sm:ml-auto"
                            onClick={() => setCompareOpen(v => !v)}
                            data-testid="compare-open-btn"
                        >
                            <GitCompare className="w-4 h-4 mr-2" />
                            {t('analyticsCompare.button')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Compare Mode — additive, inline */}
            {compareOpen && (
                <CompareMode
                    timeRange={timeRange}
                    isSuperAdmin={isSuperAdmin()}
                    onClose={() => setCompareOpen(false)}
                />
            )}

            {/* Tap Health — Super Admin only, respects per-location monitoring toggle */}
            {isSuperAdmin() && tapHealth && (
                <Card data-testid="tap-health-section">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Activity className="w-4 h-4 text-amber-500" />
                                {t('tapHealth.sectionTitle')}
                            </CardTitle>
                            <Button variant="outline" size="sm" onClick={() => navigate('/admin/tap-health')} data-testid="tap-health-open-monitor-btn">
                                {t('tapHealth.openMonitor')}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                        {tapHealth.monitored_count === 0 ? (
                            <p className="text-sm text-muted-foreground" data-testid="tap-health-none-monitored">
                                {t('tapHealth.noneMonitored')}
                            </p>
                        ) : tapHealth.alerts.length === 0 ? (
                            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400" data-testid="tap-health-all-healthy">
                                <CheckCircle className="w-4 h-4" />
                                {t('tapHealth.allHealthy')} · {t('tapHealth.monitoredCount', { count: tapHealth.monitored_count })}
                            </div>
                        ) : (
                            tapHealth.alerts.map(a => (
                                <div key={a.location_id} className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid={`tap-health-alert-${a.location_id}`}>
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-foreground">
                                                {a.location_name}{a.organization_name ? ` — ${a.organization_name}` : ''}
                                            </p>
                                            <p className="text-sm text-amber-700 dark:text-amber-300">
                                                {t('tapHealth.silentAlert', { days: tapHealth.window_days })} · {t('tapHealth.activeCards', { count: a.active_cards })} · {t('tapHealth.lastTap')}: {a.last_tap ? format(parseUtc(a.last_tap), 'MMM dd, yyyy') : t('tapHealth.never')}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">{t('tapHealth.operationalNote')}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        {tapHealth.monitored_count > 0 && tapHealth.unmonitored_count > 0 && (
                            <p className="text-xs text-muted-foreground" data-testid="tap-health-unmonitored-note">
                                {t('tapHealth.notMonitored', { count: tapHealth.unmonitored_count })}
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <Card className="stat-card" data-insight-id="analytics.kpi.total-taps" data-insight-label="Total Taps">
                    <CardContent className="p-5">
                        <p className="text-sm text-muted-foreground">{t('analytics.totalTaps')}</p>
                        <p className="text-3xl font-bold text-foreground mt-1">{taps.length}</p>
                    </CardContent>
                </Card>
                <Card className="stat-card" data-insight-id="analytics.kpi.blocked-duplicates" data-insight-label="Blocked as duplicate" data-testid="analytics-blocked-duplicates">
                    <CardContent className="p-5">
                        <p className="text-sm text-muted-foreground">{t('analytics.blockedDuplicates')}</p>
                        <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="analytics-blocked-total">
                            {blocked.blocked_total}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{t('analytics.blockedNotCounted')}</p>
                    </CardContent>
                </Card>
                <Card className="stat-card" data-insight-id="analytics.kpi.opened" data-insight-label="Opened" data-testid="analytics-opened">
                    <CardContent className="p-5">
                        <p className="text-sm text-muted-foreground">{t('analytics.opened')}</p>
                        <p className="text-3xl font-bold text-sky-600 mt-1" data-testid="analytics-opened-total">
                            {blocked.opened_total}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{t('analytics.openedNotCounted')}</p>
                    </CardContent>
                </Card>
                <Card className="stat-card" data-insight-id="analytics.kpi.avg-daily" data-insight-label="Avg Daily">
                    <CardContent className="p-5">
                        <p className="text-sm text-muted-foreground">{t('analytics.avgDaily')}</p>
                        <p className="text-3xl font-bold text-foreground mt-1">
                            {Math.round(taps.length / rangeDays)}
                        </p>
                    </CardContent>
                </Card>
                <Card className="stat-card" data-insight-id="analytics.kpi.top-platform" data-insight-label="Top Platform">
                    <CardContent className="p-5">
                        <p className="text-sm text-muted-foreground">{t('analytics.topPlatform')}</p>
                        <p className="text-xl font-bold text-foreground mt-1">
                            {getTapsByPlatform()[0]?.platform || 'N/A'}
                        </p>
                    </CardContent>
                </Card>
                <Card className="stat-card" data-insight-id="analytics.kpi.peak-hour" data-insight-label="Peak Hour">
                    <CardContent className="p-5">
                        <p className="text-sm text-muted-foreground">{t('analytics.peakHour')}</p>
                        <p className="text-3xl font-bold text-foreground mt-1">
                            {getTapsByHour().reduce((max, h) => h.taps > max.taps ? h : max, { hour: 'N/A', taps: 0 }).hour}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* BLOCKED AS DUPLICATE — per card, with reason + time, so a client can
                see WHY a repeat tap didn't move the counter instead of assuming
                the card is broken. Test-mode taps are listed separately and are
                excluded from every real statistic on this page. */}
            {(blocked.blocked_total > 0 || blocked.test_total > 0 || blocked.opened_total > 0) && (
                <Card data-testid="analytics-blocked-panel">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            {t('analytics.blockedTitle')}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">{t('analytics.blockedSubtitle')}</p>
                        {blocked.gift_generator_counting_since && (
                            <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-gg-counting-note">
                                {t('analytics.giftGeneratorCountingSince', { date: blocked.gift_generator_counting_since })}
                            </p>
                        )}
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('analytics.card')}</TableHead>
                                    <TableHead>{t('analytics.blockedDuplicates')}</TableHead>
                                    <TableHead>{t('analytics.opened')}</TableHead>
                                    <TableHead>{t('analytics.testTaps')}</TableHead>
                                    <TableHead>{t('analytics.lastBlocked')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {blocked.cards.map((c) => (
                                    <TableRow key={c.card_id} data-testid={`analytics-blocked-row-${c.card_id}`}>
                                        <TableCell className="font-medium">{c.card_id}</TableCell>
                                        <TableCell>
                                            {c.blocked > 0
                                                ? <Badge variant="outline" className="border-amber-400 text-amber-700">{c.blocked}</Badge>
                                                : <span className="text-muted-foreground">0</span>}
                                        </TableCell>
                                        <TableCell>
                                            {c.opened > 0
                                                ? <Badge variant="outline" className="border-sky-400 text-sky-700">{c.opened}</Badge>
                                                : <span className="text-muted-foreground">0</span>}
                                        </TableCell>
                                        <TableCell>
                                            {c.test > 0
                                                ? <Badge variant="outline">{c.test}</Badge>
                                                : <span className="text-muted-foreground">0</span>}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {c.items?.[0]
                                                ? `${c.items[0].label} — ${format(parseUtc(c.items[0].timestamp), 'MMM d, HH:mm')}`
                                                : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Taps Over Time */}
                <Card data-insight-id="analytics.chart.taps-over-time" data-insight-label="Taps Over Time chart">
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('analytics.tapsOverTime')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={getTapsByDay()}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fontSize: 12, fill: '#64748B' }}
                                    />
                                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                                    <Tooltip />
                                    <Line 
                                        type="monotone" 
                                        dataKey="taps" 
                                        stroke="#2563EB" 
                                        strokeWidth={2}
                                        dot={{ fill: '#2563EB' }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Taps by Employee */}
                <Card data-insight-id="analytics.chart.top-employees" data-insight-label="Top Employees chart">
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('analytics.topEmployees')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={getTapsByEmployee()} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} />
                                    <YAxis 
                                        type="category" 
                                        dataKey="name" 
                                        tick={{ fontSize: 12, fill: '#64748B' }}
                                        width={100}
                                    />
                                    <Tooltip />
                                    <Bar dataKey="taps" fill="#10B981" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Taps by Hour */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('analytics.tapsByHour')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={getTapsByHour()}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis 
                                        dataKey="hour" 
                                        tick={{ fontSize: 10, fill: '#64748B' }}
                                        interval={2}
                                    />
                                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                                    <Tooltip />
                                    <Bar dataKey="taps" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Taps by Platform */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('analytics.tapsByPlatform')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {getTapsByPlatform().map((item) => (
                                <div key={item.platform} className="flex items-center gap-3">
                                    <Badge className={`${getPlatformColor(item.platform)} min-w-[120px] justify-center`}>
                                        {item.platform}
                                    </Badge>
                                    <div className="flex-1 h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-blue-500 rounded-full"
                                            style={{ 
                                                width: `${(item.taps / Math.max(...getTapsByPlatform().map(p => p.taps))) * 100}%` 
                                            }}
                                        />
                                    </div>
                                    <span className="text-sm font-medium text-foreground min-w-[40px] text-right">
                                        {item.taps}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Taps Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('analytics.recentTaps')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {taps.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('analytics.cols.timestamp')}</TableHead>
                                        <TableHead>{t('analytics.cols.cardId')}</TableHead>
                                        <TableHead>{t('analytics.cols.employee')}</TableHead>
                                        <TableHead>{t('analytics.cols.platform')}</TableHead>
                                        <TableHead>{t('analytics.cols.location')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {taps.slice(0, 20).map((tap) => (
                                        <TableRow key={tap.id}>
                                            <TableCell className="text-muted-foreground">
                                                {format(parseUtc(tap.timestamp), 'MMM dd, HH:mm:ss')}
                                            </TableCell>
                                            <TableCell className="font-medium">{tap.card_id}</TableCell>
                                            <TableCell>{tap.employee_name || t('analytics.unassigned')}</TableCell>
                                            <TableCell>
                                                <Badge className={getPlatformColor(
                                                    tap.platform?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                                                )}>
                                                    {tap.platform?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || t('analytics.unknown')}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {getLocationName(tap.location_id)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="py-12 text-center text-muted-foreground">
                            <Wifi className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>{t('analytics.noTapsInPeriod')}</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Analytics;
